import { useTranslation } from '@klv/hooks/useTranslation';
import { tsvParse, autoType } from 'd3-dsv';
import BarChart from '@klv/molecules/bar-chart';
import LineChart from '@klv/molecules/line-chart';
import PieChart from '@klv/molecules/pie-chart';
import * as s from './styles';
import type { IChart } from './types';

const Chart = ({
  chartSettings,
}: IChart) => {
  const { i18n } = useTranslation();

  const localeAutoType = (object: any) => {
    const locale = i18n.language;
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const group = new RegExp(`[${parts.find(d => d?.type === 'group')?.value}]`, 'g');
    const decimal = new RegExp(`[${parts.find(d => d?.type === 'decimal')?.value}]`);

    const parse = (itemString: string) =>  {
      const number = (itemString = itemString.trim()
        .replace(group, '')
        .replace(decimal, '.'));
      return number;
    };

    for (const key in object) {
      const parsed = parse(object[key]);
      if (!isNaN(+parsed))
        object[key] = parsed;
    }
    return autoType(object);
  };

  const {
    backgroundColor,
    alignHeadline,
    alignSubhead,
    headline,
    subheadline,
    chartType,
    barCharts,
    lineCharts,
    pieCharts,
    disclaimer,
  } = chartSettings; 
  const barData = barCharts ? (barCharts.barChart.layout === 'vertical' ? barCharts?.barChart?.chartDataVertical : barCharts?.barChart?.chartDataHorizontal) : []; 
  return (
    <s.StyledChart $backgroundColor={backgroundColor}>
      <s.StyledGrid>
        <s.ChartHeader $textColor={backgroundColor}>
          {headline && alignHeadline === 'left' ? 
            <s.LeftHeadline forwardedAs="h2" variant="h2">{headline}</s.LeftHeadline> 
            : 
            <s.Headline forwardedAs="h2" variant="h2">{headline}</s.Headline>
          }
          {subheadline && alignSubhead === 'left' ? 
            <s.LeftSubheadline variant="paragraph2">{subheadline}</s.LeftSubheadline> 
            :
            <s.SubHeadline variant="paragraph2">{subheadline}</s.SubHeadline>
          }
        </s.ChartHeader>
        {(chartType === 'bar' && barCharts) ? (
          <s.BarCharts $multiple={barCharts?.barChart?.chartDataVertical?.length === 2}>
            {barData?.map((data, index) => {
              if (!data.chartData) {
                return null;
              }
              const parsedData = tsvParse(data.chartData, localeAutoType);    
              return (
                <s.ChartInfo key={index}>
                  {<s.BarandLineChartHeadline $textColor={backgroundColor} $alignment={alignHeadline} variant="chartHeadline">{barData[index].chartHeadline}</s.BarandLineChartHeadline>}
                  <div>
                    <BarChart
                      backgroundColor={backgroundColor}
                      data={parsedData}
                      headers={parsedData.columns}
                      layout={barCharts?.barChart.layout}
                      numberFormat={barData[index].numberFormat}
                    />
                  </div>
                </s.ChartInfo>
              );
            })}
            </s.BarCharts>
        ) : (chartType === 'line' && lineCharts) ? (
          <>          
            {lineCharts.lineChart.chartHeadline && <s.BarandLineChartHeadline $alignment={alignHeadline} $textColor={backgroundColor} variant="chartHeadline">{lineCharts.lineChart.chartHeadline}</s.BarandLineChartHeadline>}
            <LineChart
              colors={lineCharts?.lineChart.color}
              data={tsvParse(lineCharts?.lineChart.chartData, localeAutoType)}
              headers={tsvParse(lineCharts?.lineChart.chartData, autoType).columns}
              backgroundColor={backgroundColor}
              yAxisHeadline={lineCharts?.lineChart.yAxisHeadline}
              totalValues={lineCharts?.totalValues && lineCharts.totalValues}
              numberFormat={lineCharts?.lineChart.numberFormat}
            />
          </>
        ) : (chartType === 'pie' && pieCharts) ? (
          <s.PieCharts $multiple={pieCharts?.pieChart?.pieData.length === 2}>
          {pieCharts?.pieChart?.pieData?.map((data, index) => {
            if (!data.chartData) {
              return null; 
            }
            return (
                <s.ChartInfo>
                  {<s.ChartHeadline $textColor={backgroundColor} $alignment={alignHeadline} variant="chartHeadline">{data.chartHeadline}</s.ChartHeadline>}
                  <PieChart
                    key={index} 
                    backgroundColor={backgroundColor}
                    color={data.color}
                    data={tsvParse(data.chartData, autoType)}
                    headers={tsvParse(data.chartData, autoType).columns} 
                    isMultiple={pieCharts?.pieChart?.pieData.length === 2}
                  /> 
                </s.ChartInfo>
            );
          })
          } </s.PieCharts>) : <></>
        }
        {disclaimer && <s.DisclaimerText $textColor={backgroundColor} variant="disclaimer" dangerouslySetInnerHTML={{ __html: disclaimer }}/>}
      </s.StyledGrid>
    </s.StyledChart >
  );
};

export default Chart;
